import { isEqual, last, sortBy, uniq } from 'lodash'
import times from 'lodash/times'
import { Node as ProseNode } from 'prosemirror-model'
import { findChildren } from 'prosemirror-utils'
import { LineMode, isTable, isTableCell } from '.'
import { distributeValue } from '../distributeValue'
import { Map2D } from './Map2D'
import { possibleWidthsFromColumnStops } from './possibleWidthsFromColumnStops'

export type Coords = [row: number, col: number]

export class TableMap {
  private cellMap = new Map2D<number>()
  numRows: number = 0
  numCols: number = 0
  tableNodeWithPos: { posBeforeTable: number; node: ProseNode }

  private colFragments: Array<[number, number][]> | undefined = undefined
  private rowFragments: Array<[number, number][]> | undefined = undefined

  constructor(tableNodeWithPos: { posBeforeTable: number; node: ProseNode }) {
    this.tableNodeWithPos = tableNodeWithPos
    if (isNaN(tableNodeWithPos.posBeforeTable) || !isTable(tableNodeWithPos.node)) return
    this.rebuild()
  }

  private assembleMap() {
    const newCellMap = new Map2D<number>()
    const rows = findChildren(this.tableNodeWithPos.node, (child) => child.type.name === 'table_row', false)

    let numRows = 0
    let numCols = 0
    rows.forEach((rowWithPos, rowIdx) => {
      const cells = findChildren(rowWithPos.node, (child) => isTableCell(child), false)
      cells.forEach((cellWithPos, colIdx) => {
        const rowspan = cellWithPos.node.attrs.rowspan ?? 1
        const colspan = cellWithPos.node.attrs.colspan ?? 1

        let colOffset = 0
        while (newCellMap.has(rowIdx, colIdx + colOffset)) {
          colOffset++
        }

        const cellCoords = times(rowspan, (ri) =>
          times(colspan, (ci) => {
            return [rowIdx + ri, colIdx + colOffset + ci] as [number, number]
          }),
        ).flat(1)

        numRows = Math.max(numRows, rowIdx + rowspan)
        numCols = Math.max(numCols, colIdx + colspan + colOffset)

        // 2: Enter Table -> Enter Row (results in a position before `Enter Cell`)
        const cellPos = cellWithPos.pos + rowWithPos.pos + this.tableNodeWithPos.posBeforeTable + 2
        cellCoords.forEach(([row, col]) => {
          return newCellMap.set(row, col, cellPos)
        })
      })
    })
    return { cellMap: newCellMap, numRows, numCols }
  }

  /**
   * @param position A position directly before a cell
   * @returns List of coordinates of the cell directly after pos (in the form `[row, col][]`)
   */
  getCoordListOfPos(position: number): Coords[] {
    return this.cellMap.findCoordsOfValue(position)
  }

  getPosAtCoord(x: number, y: number): number | undefined {
    return this.cellMap.get(y, x)
  }

  /**
   * Return list of cell positions in column order. In a row with a cell that has a rowspan and
   * begins before that row, the position of the rowspan cell will be lower than the position of
   * cells with rowspan of 1.
   *
   * E.g.
   * ```
   *      ___________________________
   *     |      |      |             |
   *  A  |A1(1) |A2(2) |     A3(3)   |
   *     |______|______|______ ______|
   *     |      |             |      |
   *  B  |B1(4) |   B2(5)     |      |
   *     |______|______ ______|      |
   *     |      |      |      |B3(6) |
   *  C  |C1(7) |C2(8) |C3(9) |      |
   *     |______|______|______|______|
   * ```
   * Consider posList of row C. Cell B3 is at the end of row C, but the pos(number) will be
   * lower than all other cells in row C.
   * getOrderedPosListInRow(2) => [7, 8, 9, 6]
   */
  getOrderedPosListInRow(rowIdx: number): number[] {
    const posWithCols = this.cellMap.entries().flatMap(([coord, pos]) => {
      if (coord[0] === rowIdx) {
        return [[coord[1], pos] as [number, number]]
      }
      return []
    })

    return posWithCols.sort((a, b) => a[0] - b[0]).map(([col, pos]) => pos)
  }

  hasMergedCells() {
    return this.getPosBeforeCellList().length !== this.numRows * this.numCols
  }

  hasVerticalMergedCells() {
    const posListOfVeriticalMergedCells = Array.from(this.cellMap.values())
      .filter((pos, index, self) => self.indexOf(pos) !== index)
      .flatMap((pos) => {
        const cell = this.tableNodeWithPos.node.resolve(pos - this.tableNodeWithPos.posBeforeTable).node()
        if (cell?.attrs?.rowspan > 1) {
          return [pos]
        }
        return []
      })

    return posListOfVeriticalMergedCells.length > 0
  }

  /**
   * Calculates column widths of a table.
   *
   * Because of row/colspans a tables column widths cannot be calculated by simply looking at cellWidths.
   * In some situations, a column might not include a cell that has a colspan of 1.
   *
   * Algorithm is as follows:
   * 1. Loop through each cell in each row. Add each 'stop' (position where cell ends) to a list.
   * 2. Calculate widths of cells from stops, `width = stop - previousStop`.
   * 3. If count of calulated widths is less than number of columns, find columns where
   * the posLists are equal and distribute the width for those columns.
   *
   * @returns Array of column widths(number) in px.
   */
  getColumnWidths(): number[] {
    const rows = findChildren(this.tableNodeWithPos.node, (child) => child.type.name === 'table_row', false)
    let columnStops = new Set<number>()

    // 1. Loop through each cell in each row. Add each 'stop' (position where cell ends) to a list.
    rows.forEach((rowWithPos, rowIndex) => {
      let currentOffset = 0
      uniq(this.getOrderedPosListInRow(rowIndex)).forEach((cellPos) => {
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const cell = this.tableNodeWithPos.node.nodeAt(cellPos - this.tableNodeWithPos.posBeforeTable - 1)!
        const cellWidth = parseInt(cell.attrs.cellWidth)
        const nextStop = currentOffset + cellWidth
        columnStops.add(nextStop)
        currentOffset = nextStop
      })
    })
    // 2. Calculate widths of cells from stops, `width = stop - previousStop`.
    const widths = possibleWidthsFromColumnStops(columnStops)

    // 3. If count of calulated widths is less than number of columns, find columns where
    // the posLists are equal and distribute the width for those columns.
    if (widths.length !== this.numCols) {
      let currentPosList: number[] = [],
        nextPosList: number[] = [],
        colIndex = 0,
        acc = 1
      while (colIndex < this.numCols || acc > 1) {
        currentPosList = this.getPosListInColumn(colIndex)
        nextPosList = this.getPosListInColumn(colIndex + 1)
        if (isEqual(currentPosList, nextPosList)) {
          acc++
        } else if (acc > 1) {
          const startingColumnIndex = colIndex - acc + 1
          const colReplacements = distributeValue(widths[startingColumnIndex], acc)
          widths.splice(startingColumnIndex, 1, ...colReplacements)
          acc = 1
        }
        colIndex++
      }
    }
    return widths
  }

  getPosListInColumn(colIdx: number): number[] {
    // TODO: Dedupe the output, make sure that no one relies on duplicated values (i.e. getIncludedBorderIndexes)
    return this.cellMap.entries().flatMap(([coord, pos]) => {
      if (coord[1] === colIdx) {
        return [pos]
      }
      return []
    })
  }

  /**
   * Given an array of unique, sequential numbers which may or may not contain gaps
   * converts that array into a list of "ranges"
   *
   * @param indexList A list of row/column indexes in the form `[0,1,2,7,8,9]`
   * @returns An array containing ranges, in the form `[start: number, end: number]`
   * @example
   * ```
   * indexToFragments([0,1,2,3,4,5,9,10,11,15,16,17]) // returns `[[0,5], [9,11], [15,17]]`
   * ```
   */
  private indexToFragments(indexList: number[]): Array<[number, number]> {
    if (indexList.length === 0) {
      return []
    }
    const firstNumber = indexList[0]
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const lastNumber = last(indexList)!
    let index = 0
    for (let currentNumber = firstNumber; currentNumber < lastNumber; currentNumber++) {
      if (indexList[index] !== currentNumber) {
        const processedArray = indexList.slice(0, index)
        const unprocessedArray = indexList.slice(index, indexList.length)

        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        return [[processedArray[0], last(processedArray)!], ...this.indexToFragments(unprocessedArray)]
      }
      index++
    }
    return [[firstNumber, lastNumber]]
  }

  /**
   * Returns a list of row/column indexes that either start or end in the specified `borderIdx`
   * (which means their border gets drawn at that `borderIdx`)
   *
   * Skips row/column indexes that span across the `borderIdx`
   * @param mode Are you looking for column borders or row borders
   * @param borderIdx Which row/column index
   *
   * @returns A list of row/column indexes in the form `[0,1,2,7,8,9]`
   */
  private getIncludedBorderIndexes(mode: LineMode, borderIdx: number) {
    const cellPosList =
      mode === LineMode.Column ? this.getPosListInColumn(borderIdx) : this.getOrderedPosListInRow(borderIdx)
    // Sort pos list to process in order they appear in row/column and not in order of pos.
    const cellPosListSorted = sortBy(cellPosList, (pos) => {
      const [y, x] = this.getCoordListOfPos(pos)[0]
      return mode === LineMode.Column ? y : x
    })
    return cellPosListSorted.reduce((acc, pos, rowIndex) => {
      // TODO: Fix this function to not rely on duplicated getPos entries
      const cellCoordList = this.getCoordListOfPos(pos)

      // Determine the top/bottom (LineMode.Row) or left/right (LineMode.Column) borders of the cell
      const cellBounds = cellCoordList.reduce(
        (acc, cellCoords) => {
          const [y, x] = cellCoords
          if (mode === LineMode.Column) {
            acc[0] = x < acc[0] ? x : acc[0]
            acc[1] = x + 1 > acc[1] ? x + 1 : acc[1]
          } else {
            acc[0] = y < acc[0] ? y : acc[0]
            acc[1] = y + 1 > acc[1] ? y + 1 : acc[1]
          }
          return acc
        },
        [Infinity, -Infinity] as [number, number],
      )
      if (cellBounds.includes(borderIdx)) {
        acc.push(rowIndex)
      }
      return acc
    }, [] as number[])
  }

  getColFragments(): Array<[number, number][]> {
    if (this.colFragments) {
      return this.colFragments
    }
    let colFragments: Array<[number, number][]> = []
    for (let colIdx = 0; colIdx < this.numCols; colIdx++) {
      const includedColIndexes = this.getIncludedBorderIndexes(LineMode.Column, colIdx)
      colFragments.push(this.indexToFragments(includedColIndexes))
    }
    this.colFragments = [...colFragments, [[0, this.numRows - 1]]]
    return this.colFragments
  }

  getRowFragments(): Array<[number, number][]> {
    if (this.rowFragments) {
      return this.rowFragments
    }
    let rowFragments: Array<[number, number][]> = []
    for (let rowIdx = 0; rowIdx < this.numRows; rowIdx++) {
      const includedRowIndexes = this.getIncludedBorderIndexes(LineMode.Row, rowIdx)
      rowFragments.push(this.indexToFragments(includedRowIndexes))
    }
    this.rowFragments = [...rowFragments, [[0, this.numCols - 1]]]
    return this.rowFragments
  }

  getPosBeforeCellList(): number[] {
    return uniq([...this.cellMap.values()])
  }

  getAllKnownCoords(): Coords[] {
    return this.cellMap.keys()
  }

  getCoordListOfCellIndex(cellIndex: number): Coords[] {
    const cellPos = this.getPosBeforeCellList()[cellIndex]
    return this.getCoordListOfPos(cellPos)
  }

  // Resolves the span offset if the position falls into a cell with colspan or
  // rowspan (or both).  Colloquially: "How far am I away from the start of this
  // cell, if am at at these coordinates at the moment?""
  //
  // Consider this table:
  // +-----+---------+-----+
  // | Abc | def       ghi |
  // +-----+---------+-----+
  // | jkl | mno     | pqr |
  // +     +---------+-----+
  // | stu | vwx     | yz  |
  // +-----+---------+-----+
  //
  // getSpanOffsetsAtCoord(0, 0) => [0, 0]
  // getSpanOffsetsAtCoord(0, 1) => [0, 0]
  // getSpanOffsetsAtCoord(0, 2) => [0, 1] // same row, but one column away
  // getSpanOffsetsAtCoord(1, 0) => [0, 0]
  // getSpanOffsetsAtCoord(2, 0) => [1, 0] // same column, but one row away
  //
  getSpanOffsetsAtCoord(row: number, col: number): [number, number] {
    // returns rowOffset, colOfffset
    const basePos = this.getPosAtCoord(col, row)
    let rowOffset = 0,
      colOffset = 0

    for (let rowPointer = row - 1; rowPointer >= 0; rowPointer--) {
      if (this.getPosAtCoord(col, rowPointer) !== basePos) break
      rowOffset += 1
    }

    for (let colPointer = col - 1; colPointer >= 0; colPointer--) {
      if (this.getPosAtCoord(colPointer, row) !== basePos) break
      colOffset += 1
    }

    return [rowOffset, colOffset]
  }

  rebuild() {
    const assemblyResult = this.assembleMap()
    this.cellMap = assemblyResult.cellMap
    this.numRows = assemblyResult.numRows
    this.numCols = assemblyResult.numCols
  }
}
